import { FunctionComponent, useState, useEffect, useCallback, useRef } from 'react'
import DashboardLayout from '../../components/layout/layout'
import { Alert, Button, Grid, MenuItem, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { StyledSelect } from '../../components/ui/formInput'
import { useGetCompanyChannelPartner } from '../../hooks/api/oms'
import { useGetOnBehalfUsers } from '../../hooks/api/onBehalfUser'
import StorageService from '../../services/storageService'
import useRequest from '../../hooks/useRequest'

type ValidateField = {
  invalid: boolean;
  error: string;
};

type ValidateFieldsState = {
  custNum: ValidateField;
};

const OnlineOrderEntry: FunctionComponent = () => {
  const navigate = useNavigate();
  const request = useRequest();
  const [custNum, setCustNum] = useState('');
  const [error, setError] = useState('');
  const [disabled, setDisabled] = useState<boolean>(false);
  const [isOnBehalfUser, setIsOnBehalfUser] = useState<boolean>(false);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState<boolean>(true);
  const [onBehalfCustomers, setOnBehalfCustomers] = useState<any[]>([]);
  // Get current user info to check access level
  const currentUser = StorageService.getUserInfo();
  const isCPUser = currentUser?.accessLevel === 'CP';
  
  // Call API for all users (including CP users) to get company list and on-behalf users
  const {data: customers} = useGetCompanyChannelPartner(!isCPUser);
  // Enable on-behalf users API for all users (including CP users) to check if they're in the list
  const {data: onBehalfUsers} = useGetOnBehalfUsers(!isCPUser);
  const hasCheckedOnBehalf = useRef(false);

  // Check if current user is on-behalf user and get their company accounts
  // This integrates with the on-behalf users management system while preserving existing functionality
  // SAFE INTEGRATION: All existing functionality is preserved, only adds on-behalf user detection
  // ORIGINAL BEHAVIOR: Non-on-behalf users go directly to header page
  useEffect(() => {
    // Wait for on-behalf users API to load (required for both CP and Barksdale users)
    //if (hasCheckedOnBehalf.current || !onBehalfUsers) return;
    
    // For CP users, skip API calls and go directly to header page
    if (isCPUser) {
      if (hasCheckedOnBehalf.current) return;
      hasCheckedOnBehalf.current = true;
      setIsOnBehalfUser(false);
      setIsLoadingCustomers(false);
      // CP users don't need custNum parameter - backend will use their assigned customer
      navigate('/order-management-system/online-order-entry/header');
      return;
    }
    
    
    // for non-CP users, wait for both APIs to load
    if (hasCheckedOnBehalf.current || !onBehalfUsers || !customers) return;
    
    const checkOnBehalfUser = async () => {
      try {
        setIsLoadingCustomers(true);
        hasCheckedOnBehalf.current = true; // Mark as checked
        
        // Get current user info
        const currentUser = StorageService.getUserInfo();
        if (!currentUser) {
          setIsOnBehalfUser(false);
          return;
        }

        // Check if current user is in the on-behalf users list
        const currentOnBehalfUser = onBehalfUsers?.find(user => user.userId === currentUser.userId);
        
        console.log('Current user ID:', currentUser.userId);
        console.log('Found on-behalf user:', currentOnBehalfUser);
        console.log('Is active:', currentOnBehalfUser?.active);
        console.log('Allowed company IDs:', currentOnBehalfUser?.allowedCompanyIds);
        
        // If user is not found in on-behalf users list, redirect to header (original behavior)
       
        if (!currentOnBehalfUser) {
          console.log('User not found in on-behalf users list, redirecting to header page');
         
          navigate('/order-management-system/online-order-entry/header');
          return;
        }
        
        if (currentOnBehalfUser && currentOnBehalfUser.active) {
          // User is on-behalf user
          setIsOnBehalfUser(true);
          
          // Use allowedCompanies from backend (includes CP and CUST types with custNum)
          if (currentOnBehalfUser.allowedCompanies && currentOnBehalfUser.allowedCompanies.length > 0) {
            // Use allowedCompanies directly - no mapping needed, includes both CP and CUST types
            const companyDetails = currentOnBehalfUser.allowedCompanies
              .filter((c: any) => c.custNum) // Only include those with customer numbers
              .map((c: any) => ({
                custNum: c.custNum,
                companyName: c.companyName,
                companyId: c.companyId,
                companyType: c.companyType
              }));
            
            console.log('Company details for on-behalf user (from allowedCompanies):', companyDetails);
            setOnBehalfCustomers(companyDetails);
          } 
          // Fallback: Use allowedCompanyIds if allowedCompanies is not available (old behavior)
          else if (currentOnBehalfUser.allowedCompanyIds && currentOnBehalfUser.allowedCompanyIds.length > 0) {
            // OLD CODE - Kept as fallback in case allowedCompanies is not returned
            // Map company IDs to customer numbers for API calls
            // Note: This may not work for CUST type companies as they may not be in channelPartners list
            const companyDetails = currentOnBehalfUser.allowedCompanyIds.map((companyId: number) => {
              // Find the company details from the channel partners list
              const companyInfo = customers?.channelPartners?.find((comp: any) => comp.id === companyId);
              return {
                custNum: companyInfo?.custNum || companyId.toString(), // Use actual customer number (e.g., "286") for API calls
                companyName: companyInfo?.companyName || `Company Account ${companyId}`, // This will be shown in dropdown (display text)
                companyId: companyId, // Internal reference (e.g., 6 for Alpha Process)
                companyType: companyInfo?.companyType || 'CP'
              };
            });
            
            console.log('Company details for on-behalf user (fallback from allowedCompanyIds):', companyDetails);
            setOnBehalfCustomers(companyDetails);
          } else {
            // User is on-behalf user but has no company restrictions - use existing API as fallback
            console.log('On-behalf user with no company restrictions, using existing API');
            try {
              const response = await request({
                method: 'get',
                url: '/api/v1/channel-partners/oentry/customers',
                config: {},
                completeResponse: false,
              });
              
              if (response.success && response.customers && response.customers.length > 0) {
                setOnBehalfCustomers(response.customers);
              } else {
                setOnBehalfCustomers(customers?.channelPartners || []);
              }
            } catch (apiErr: any) {
              console.warn('Fallback API failed, using channel partners:', apiErr);
              setOnBehalfCustomers(customers?.channelPartners || []);
            }
          }
        } else {
          // User is not on-behalf user - redirect directly to order header (original behavior)
          console.log('User is not on-behalf user, redirecting to header page');
          navigate('/order-management-system/online-order-entry/header');
          return;
        }
      } catch (err: any) {
        console.error('Error checking on-behalf user status:', err);
        console.error('Error details:', {
          message: err.message,
          stack: err.stack,
          currentUser: StorageService.getUserInfo(),
          onBehalfUsers: onBehalfUsers,
          customers: customers
        });
        setError(`Error loading user permissions: ${err.message || 'Unknown error'}`);
        setIsOnBehalfUser(false);
      } finally {
        setIsLoadingCustomers(false);
      }
    };

    checkOnBehalfUser();
  }, [onBehalfUsers, customers, navigate, request]); // Add dependencies

  const formValidation: ValidateFieldsState = {
    custNum: { invalid: false, error: '' }
  };
  const [validateFields, setValidateFields] = useState<ValidateFieldsState>(formValidation);

  const validateForm = () => {
    let valid = true;
    const errors = { ...validateFields };

    if (!custNum?.trim()) {
      errors.custNum = { invalid: true, error: isOnBehalfUser ? 'Company account is required' : 'Customer is required' };
      valid = false;
    } else {
      errors.custNum = { invalid: false, error: '' };
    }

    setValidateFields(errors);
    return valid;
  };

  const handleNext = () => {
    setValidateFields(formValidation);
    if (!validateForm()) {
      return;
    }
    setError('');
    setDisabled(true);
    StorageService.setOEntryAdminCustomer(custNum);
    StorageService.clearCart();
    
    if (isOnBehalfUser) {
      // For on-behalf users, go to order header with selected customer
      navigate(`/order-management-system/online-order-entry/header?custNum=${custNum}`);
    } else {
      // For regular users, go directly to order header (they can only use their assigned customer)
      navigate('/order-management-system/online-order-entry/header');
    }
  };

  return (
    <DashboardLayout title={"Orders Management"} initialBreadcrumbs={"Channel Partners"} levelBreadcrumbs={"Orders Management"} breadcrumbs={'Online Order Entry'} activeMenu={'oms'} containerTitle={"Online Order Entry"}>
      {isLoadingCustomers ? (
        <Grid container spacing={2}>
          <Grid size={12}>
            <Typography variant="body1">Loading company accounts and checking user permissions...</Typography>
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid size={4}>
            <Typography variant="subtitle1" color="#000000" fontWeight="bold" gutterBottom>
              {isOnBehalfUser ? 'On-Behalf Order Entry' : 'Barksdale Online Ordering'}
            </Typography>
            <StyledSelect
              label={isOnBehalfUser ? "Select Company Account" : "Select Customer"}
              value={custNum}
              onChange={(e: any) => setCustNum(e.target.value)}
              error={validateFields.custNum.invalid}
              helperText={validateFields.custNum.error}
            >
              <MenuItem key={'default'} value={''}>
                {isOnBehalfUser ? 'Select Company Account' : 'Select Customer'}
              </MenuItem>
              {isOnBehalfUser ? (
                // Show on-behalf customers with customer account numbers
                onBehalfCustomers.map((cust: any) => (
                  <MenuItem key={cust.custNum || cust.companyId} value={cust.custNum || cust.companyId}>
                    {`${cust.custNum} - ${cust.companyName}`}
                  </MenuItem>
                ))
              ) : (
                // Show regular BARKSDALE customers
                customers?.channelPartners?.map((cust: any) => (
                  <MenuItem key={cust.custNum} value={cust.custNum}>
                    {cust.companyName}
                  </MenuItem>
                ))
              )}
            </StyledSelect>
          </Grid>
          <Grid size={12}>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Grid>
          <Grid size={12}>
            <Button disabled={disabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '110px', marginRight: '25px' }} onClick={handleNext}>
              NEXT
            </Button>
            <Button disabled={disabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '110px' }} onClick={() => navigate(-1)}>
              BACK
            </Button>
          </Grid>
        </Grid>
      )}
    </DashboardLayout>
  )
}

export default OnlineOrderEntry